import { useState } from 'react';
import { Search, Filter, CheckCircle, XCircle, Trash2, Eye, Check, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AdminRejectionModal from '../../admin-rejection-modal';

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const mockUsers = [
    {
      id: 1,
      name: 'Carlos Martínez',
      email: 'carlos.martinez@email.com',
      phone: '+57 312 456 7890',
      membershipTier: 'Emprendedor',
      status: 'pending',
      registrationDate: '2025-01-15',
      paymentProof: 'https://via.placeholder.com/400x600/4F46E5/FFFFFF?text=Comprobante+Pago'
    },
    {
      id: 2,
      name: 'María González',
      email: 'maria.gonzalez@email.com',
      phone: '+57 301 234 5678',
      membershipTier: 'Elite',
      status: 'active',
      registrationDate: '2025-02-20',
      paymentProof: 'https://via.placeholder.com/400x600/7C3AED/FFFFFF?text=Comprobante+Pago'
    },
    {
      id: 3,
      name: 'Juan Ramírez',
      email: 'juan.ramirez@email.com',
      phone: '+57 315 678 9012',
      membershipTier: 'Explorador',
      status: 'rejected',
      registrationDate: '2025-03-10',
      paymentProof: 'https://via.placeholder.com/400x600/3B82F6/FFFFFF?text=Comprobante+Pago'
    }
  ];

  const membershipColors = {
    'Explorador': 'bg-blue-100 text-blue-800',
    'Emprendedor': 'bg-purple-100 text-purple-800',
    'Elite': 'bg-amber-100 text-amber-800'
  };

  const statusConfig = {
    'pending': { label: 'Pendiente', color: 'text-yellow-600 bg-yellow-50', icon: XCircle },
    'active': { label: 'Activa', color: 'text-green-600 bg-green-50', icon: CheckCircle },
    'rejected': { label: 'Rechazada', color: 'text-red-600 bg-red-50', icon: XCircle }
  };

  const handleActivateUser = (userId) => {
    console.log('Activate user:', userId);
    // Logic to activate user account
  };

  const handleRejectUser = (userId) => {
    const user = mockUsers?.find(u => u?.id === userId);
    setSelectedUser(user);
    setRejectionModalOpen(true);
  };

  const handleConfirmRejection = (rejectionData) => {
    console.log('Rejection confirmed:', rejectionData);
    // Logic to update user status to 'rejected' and save observations
    // In real implementation, this would call an API endpoint
    alert(`Usuario ${rejectionData?.userId} rechazado. Observaciones guardadas.`);
  };

  const handleDeleteUser = (userId) => {
    console.log('Delete user:', userId);
    // Logic to delete user account
  };

  const handleViewPaymentProof = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">
          Gestión de Usuarios
        </h1>
        <p className="text-muted-foreground">
          Administra membresías, suspensiones y modificaciones de usuarios
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            type="text"
            placeholder="Buscar por nombre, email o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
            leftIcon={<Search className="w-4 h-4 text-muted-foreground" />}
          />
        </div>
        <Select
          value={filterTier}
          onChange={(e) => setFilterTier(e?.target?.value)}
          className="w-full sm:w-48"
        >
          <option value="all">Todas las Membresías</option>
          <option value="explorador">Explorador</option>
          <option value="emprendedor">Emprendedor</option>
          <option value="elite">Elite</option>
        </Select>
        <Button iconName="Filter" variant="outline">
          Filtros
        </Button>
      </div>
      <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Contacto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Membresía
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Comprobante
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Acciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Estado
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {mockUsers?.map((user) => {
                const StatusIcon = statusConfig?.[user?.status]?.icon;
                return (
                  <tr key={user?.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-sm font-semibold text-primary">
                            {user?.name?.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-foreground">{user?.name}</p>
                          <p className="text-xs text-muted-foreground">{user?.registrationDate}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <p className="text-sm text-foreground">{user?.email}</p>
                      <p className="text-xs text-muted-foreground">{user?.phone}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${membershipColors?.[user?.membershipTier]}`}>
                        {user?.membershipTier}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewPaymentProof(user?.paymentProof)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Ver comprobante de pago"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="text-sm font-medium">Ver Imagen</span>
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleActivateUser(user?.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors"
                          title="Activar cuenta"
                          disabled={user?.status === 'active'}
                        >
                          <Check className="w-4 h-4" />
                          <span className="text-xs font-medium">Activar</span>
                        </button>
                        <button
                          onClick={() => handleRejectUser(user?.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-lg transition-colors"
                          title="Rechazar activación"
                        >
                          <X className="w-4 h-4" />
                          <span className="text-xs font-medium">Rechazar</span>
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user?.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="Eliminar cuenta"
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="text-xs font-medium">Eliminar</span>
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${statusConfig?.[user?.status]?.color}`}>
                        {StatusIcon && <StatusIcon className="w-4 h-4" />}
                        <span className="text-sm font-medium">{statusConfig?.[user?.status]?.label}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Image Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div 
            className="relative bg-white rounded-lg p-4 max-w-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e?.stopPropagation()}
          >
            <button
              onClick={closeImageModal}
              className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <img 
              src={selectedImage} 
              alt="Comprobante de pago" 
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Rejection Modal */}
      <AdminRejectionModal
        isOpen={rejectionModalOpen}
        onClose={() => {
          setRejectionModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onConfirmRejection={handleConfirmRejection}
      />
    </div>
  );
};

export default UserManagement;